import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { brandHeroGradient } from '../../../theme/styleHelpers';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ---------------------------------------------------------------------------
// Variant data
// ---------------------------------------------------------------------------

type Variant = 'general' | 'insurance';

interface PillarData {
  heading: string;
  body: string;
}

interface AudienceItem {
  label: string;
  description: string;
}

interface VariantContent {
  heroH1: string;
  heroSub: string;
  pillars: [PillarData, PillarData, PillarData];
  audienceHeading: string;
  audience: AudienceItem[];
}

const VARIANT_CONTENT: Record<Variant, VariantContent> = {
  general: {
    heroH1: 'Sales content sharing 80–90% cheaper than competing systems',
    heroSub:
      'Concurrent-user pricing. Multi-org by design. Add your whole team — internal reps, channel partners, resellers, external collaborators. Pay only for active usage.',
    pillars: [
      {
        heading: '80–90% Less Cost',
        body: 'Concurrent-user pricing charges for how many people are using the platform at one time — not how many accounts you\'ve provisioned. A 200-person team with 20 concurrent users costs 85–93% less than the same team on a per-seat platform.',
      },
      {
        heading: 'Multi-Org by Design',
        body: 'Adding a partner, reseller, or external collaborator is the same action as adding an internal user. No IT ticket. No licensing call. One workspace for your whole network — regardless of how many companies are involved.',
      },
      {
        heading: 'Start in Minutes',
        body: 'No implementation project. No onboarding fees. No dedicated admin required. Your team can be sharing content the same day you sign up.',
      },
    ],
    audienceHeading: 'Built for teams where content crosses company lines:',
    audience: [
      { label: 'Channel & partner programs', description: 'resellers, agents, distributors who need access to your latest materials' },
      { label: 'Distributed sales teams', description: 'field reps, remote offices, regional teams with varying usage levels' },
      { label: 'Franchise & multi-location businesses', description: 'separate legal entities that need shared content access' },
      { label: 'Insurance distribution networks', description: 'carriers, MGAs, and independent agents sharing rate sheets, guides, and compliance docs' },
      { label: 'Any team where most members use content occasionally', description: 'from daily power users to quarterly participants, all at one fair price' },
    ],
  },
  insurance: {
    heroH1: 'Affordable content sharing for insurance distribution networks.',
    heroSub:
      'Concurrent-user pricing for carriers, MGAs, and independent agents. Add your entire producer network. Pay only for active usage. Typically 80–90% less than per-seat alternatives.',
    pillars: [
      {
        heading: '80–90% Less Cost',
        body: 'A 400-agent network with 20 concurrent users costs 80–90% less than licensing 400 seats. That\'s the difference between $160K+/year and $10–18K/year.',
      },
      {
        heading: 'Built for Multi-Org Networks',
        body: 'Every agent is a separate legal entity. Adding a producer from any agency is the same action as adding an internal user. No IT ticket. No licensing call.',
      },
      {
        heading: 'Start in Minutes',
        body: 'No implementation project. No onboarding program. Share rate sheets, product guides, and compliance docs with your network the same day.',
      },
    ],
    audienceHeading: 'Built for insurance distribution:',
    audience: [
      { label: 'Carriers', description: 'distributing content to independent agent networks (200–1,000+ producers)' },
      { label: 'MGAs', description: 'sharing product guides, rate sheets, and marketing materials across appointed agencies' },
      { label: 'Wholesale brokers', description: 'managing surplus lines submissions and binding authority documentation' },
      { label: 'Agency networks & clusters', description: 'coordinating content across independently owned member agencies' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Pricing table data — identical for both variants
// ---------------------------------------------------------------------------

const HOW_IT_WORKS_STEPS = [
  { n: 1, bold: 'Create an organization and add a free license.', rest: ' All features. No credit card. No time limit.' },
  { n: 2, bold: 'Add your team.', rest: ' Internal reps, partners, resellers — anyone who needs access. No seat limits.' },
  { n: 3, bold: 'Share content.', rest: ' Upload materials. Your team has access immediately.' },
];

const PRICING_ROWS = [
  { label: 'Annual cost (200 users)', enterprise: '$120,000+', fileSharing: '$17,000–$34,000', tierOne: '$5,000–$7,000' },
  { label: 'Pricing model', enterprise: 'Per assigned seat', fileSharing: 'Per user ($7–$14/mo)', tierOne: 'Concurrent users only' },
  { label: 'Setup / implementation', enterprise: '$15,000–$45,000', fileSharing: 'None', tierOne: 'None' },
  { label: 'Multi-org support', enterprise: 'Limited / workaround', fileSharing: 'None', tierOne: 'Native' },
  { label: 'Content organization', enterprise: 'Purpose-built', fileSharing: 'Generic folders', tierOne: 'Purpose-built' },
  { label: 'Free license available', enterprise: 'No (14-day trial)', fileSharing: 'No', tierOne: 'Yes — all features, never expires' },
  { label: 'Time to launch', enterprise: 'Weeks to months', fileSharing: 'Minutes', tierOne: 'Minutes' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const CTA_TEXT = 'Start Free — All Features, No Credit Card';
const CTA_CAPTION = 'Free license includes all features and never expires.';

const CtaButton: React.FC<{ fullWidth?: boolean; onClick: () => void }> = ({ fullWidth, onClick }) => (
  <Button
    variant="contained"
    color="primary"
    size="large"
    fullWidth={fullWidth}
    onClick={onClick}
    sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}
  >
    {CTA_TEXT}
  </Button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface LandingPageProps {
  variant: Variant;
}

const LandingPage: React.FC<LandingPageProps> = ({ variant }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const content = VARIANT_CONTENT[variant];

  const handleCta = () => navigate('/register');

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ------------------------------------------------------------------ */}
      {/* Brand bar — logo + name, no nav links                               */}
      {/* ------------------------------------------------------------------ */}
      <Box
        component="header"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', color: 'inherit' }}
        >
          <Box
            component="img"
            src="/assets/tier-one-content_icon_dynamic-layers_64.png"
            alt="Tier One Content"
            sx={{ width: 32, height: 32, objectFit: 'contain' }}
          />
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Tier One Content
          </Typography>
        </Box>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Hero                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, mb: 2, lineHeight: 1.2 }}
          >
            {content.heroH1}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4, maxWidth: 600, mx: 'auto' }}>
            {content.heroSub}
          </Typography>
          <Box sx={{ maxWidth: isMobile ? '100%' : 360, mx: 'auto', mb: 1 }}>
            <CtaButton fullWidth onClick={handleCta} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {CTA_CAPTION}
          </Typography>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 2 — Three value pillars                                     */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {content.pillars.map((pillar) => (
              <Grid item xs={12} md={4} key={pillar.heading}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" component="h3" fontWeight={700} gutterBottom>
                    {pillar.heading}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pillar.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 3 — Pricing comparison table                                */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '28%' }} />
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Enterprise Platform</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>File Sharing</TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }}
                  >
                    Tier One Content
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PRICING_ROWS.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{row.label}</TableCell>
                    <TableCell align="center">{row.enterprise}</TableCell>
                    <TableCell align="center">{row.fileSharing}</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#eff6ff', fontWeight: 600, color: '#1d4ed8' }}>
                      {row.tierOne}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 4 — How it works                                            */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="sm">
          <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
            Up and running in three steps:
          </Typography>
          {HOW_IT_WORKS_STEPS.map((step) => (
            <Box key={step.n} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2.5 }}>
              <Box
                sx={(theme) => ({
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: brandHeroGradient(theme),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: '0.875rem',
                })}
              >
                {step.n}
              </Box>
              <Typography variant="body1" sx={{ pt: 0.5 }}>
                <strong>{step.bold}</strong>{step.rest}
              </Typography>
            </Box>
          ))}
          <Box sx={{ mt: 4 }}>
            <CtaButton fullWidth={isMobile} onClick={handleCta} />
          </Box>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 5 — Who this is for                                         */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
            {content.audienceHeading}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            {content.audience.map((item) => (
              <Box key={item.label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircleOutlineIcon sx={{ color: 'secondary.main', mt: 0.25, flexShrink: 0 }} />
                <Typography variant="body1">
                  <strong>{item.label}</strong>
                  {' — '}
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 6 — Address the main objection                              */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ bgcolor: '#eff6ff', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Paper variant="outlined" sx={{ p: 4, mb: 4, borderColor: '#dbeafe' }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
              "What's the catch?"
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              There isn't one. The free license includes every feature. It never expires. No credit card required.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The free license is ideal for trying out the platform quickly with no cost or risk. It's also a great
              way to set up new teams and small teams may even find it perfect for all their needs.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We'd rather you use it for real and see the value than push you through a sales process built around
              artificial urgency.
            </Typography>
          </Paper>
          <Box sx={{ textAlign: 'center' }}>
            <CtaButton fullWidth={isMobile} onClick={handleCta} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {CTA_CAPTION}
            </Typography>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
